import { TOKEN_COOKIE, TOKEN_MAX_AGE } from '~/utils/auth'
import type {
  AdminProfileInterface,
  LoginPayloadInterface,
  LoginResponseInterface,
} from '~/utils/auth'

/**
 * admin 認證狀態。
 *
 * token 以 store 內的 `token` ref 為單一來源（同步持久化到 cookie），profile 留在記憶體。
 * `$api` plugin 透過本 store 讀 token，避免「剛登入寫入 cookie、緊接著的請求新建
 * useCookie 卻還讀不到（document.cookie 尚未同步）」的時序問題 —— 登入後立刻抓 profile
 * 就是踩這個坑。pinia store 是 singleton，login 寫入 token.value 後，onRequest 從同一個
 * store 讀到的就是新值。
 *
 * Token renewal：
 * - `renewToken()` 用 raw `$fetch`（不經 `$api`）避免 renew 自己 401 觸發無限循環。
 * - 並發 in-flight singleton：多個 caller 同時呼叫只會發一次 request。
 * - `scheduleRenew()` 算剩餘時間排 setTimeout 主動續命；clearSession 時清 timer。
 */
export const useAuthStore = defineStore('auth', () => {
  // 在 setup 階段一次捕捉所有 Nuxt composables — store 的 method 會在後續非同步流程中被呼叫
  // （$api onResponseError / renewToken / clearSession 都在 await 之後），
  // 那時 Nuxt instance context 已丟失，直接呼叫 useCookie / useRuntimeConfig / useFingerprint 會拋
  // "A composable that requires access to the Nuxt instance was called outside of a plugin..."。
  // 對 useCookie 這種 SSR 要寫 response header 的 composable，捕捉 nuxtApp 用 runWithContext 包起來。
  const nuxtApp = useNuxtApp()
  const runtimeConfig = useRuntimeConfig()
  const fingerprint = useFingerprint()

  const profile = ref<AdminProfileInterface | null>(null)
  const token = ref<string | null>(useCookie<string | null>(TOKEN_COOKIE).value ?? null)

  // 並發保護：多個 caller 同時 renew 只發一次 request
  let renewInFlight: Promise<void> | null = null
  // proactive renewal timer
  let renewTimer: ReturnType<typeof setTimeout> | null = null

  /**
   * 寫入 token：同步更新記憶體 ref 與 cookie（cookie maxAge 對齊登入時效）。
   *
   * useCookie 用 nuxtApp.runWithContext 包起來 — persistToken 常在 renewToken / clearSession
   * 這種 await 後的路徑被呼叫，直接 useCookie 會拋「composable outside setup」。
   * 若沒寫進 response Set-Cookie，SSR 上就會出現「clearSession 明明跑了、但 client 帶著 stale
   * cookie 一直重進來 → HTTP redirect loop」。
   */
  function persistToken(value: string | null, maxAge: number) {
    token.value = value
    nuxtApp.runWithContext(() => {
      useCookie<string | null>(TOKEN_COOKIE, {
        maxAge,
        sameSite: 'lax',
        secure: !import.meta.dev,
      }).value = value
    })
  }

  /** 清除排定的 renew timer。 */
  function cancelRenew() {
    if (renewTimer !== null) {
      clearTimeout(renewTimer)
      renewTimer = null
    }
  }

  /**
   * 排定下一次 renew。算 surplus → 用 pickRenewDelayMs 決定 delay：
   *  - `0`：立即觸發 renew（surplus 已在 window 內或過期）
   *  - `> 0`：setTimeout 延遲觸發
   *  - `-1`：不排（surplus > 1 day，例如半年 token；reload 時會再評估）
   *
   * 重排前一律先 cancelRenew 避免重複排程。
   */
  function scheduleRenew() {
    cancelRenew()
    // SSR 不排 timer；用 typeof window 判斷以兼容 vitest（jsdom 環境
    // window 存在但 import.meta.client 是 module-level static，stub 無法跨模組）
    if (typeof window === 'undefined') return
    const surplus = getTokenSurplusSec(token.value)
    const delay = pickRenewDelayMs(surplus)
    if (delay < 0) return // 不排
    if (delay === 0) {
      void renewToken().catch(() => {})
      return
    }
    renewTimer = setTimeout(() => {
      void renewToken().catch(() => {})
    }, delay)
  }

  /**
   * 呼叫後端 renew endpoint 換新 token。並發呼叫共用同一個 in-flight Promise。
   * 用 raw `$fetch`（不經 `$api`）避免 renew 自己 401 觸發 retry 循環。
   * 成功時 persistToken 並重排下次 renew。
   */
  function renewToken(): Promise<void> {
    if (renewInFlight) return renewInFlight
    renewInFlight = doRenew().finally(() => { renewInFlight = null })
    return renewInFlight
  }

  async function doRenew(): Promise<void> {
    const current = token.value
    if (!current) throw new Error('renewToken: no token')
    const res = await $fetch<{ token: string }>('/admin/account/renew', {
      baseURL: `${runtimeConfig.public.apiBase}/api`,
      method: 'PUT',
      retry: 0,
      headers: {
        Authorization: `Bearer ${current}`,
        Fingerprint: fingerprint,
      },
    })
    // 用 surplus 推估 maxAge（保留原 token 的長 / 短期屬性）
    const surplus = getTokenSurplusSec(res.token)
    const maxAge = surplus > TOKEN_MAX_AGE.default
      ? TOKEN_MAX_AGE.keepLogin
      : TOKEN_MAX_AGE.default
    persistToken(res.token, maxAge)
    scheduleRenew()
  }

  /** 清除本地登入狀態（token + profile + renew timer）。 */
  function clearSession() {
    cancelRenew()
    persistToken(null, 0)
    profile.value = null
  }

  /** 帳密登入：取得 token、抓 profile。失敗時 $api 會 throw（由呼叫端處理）。 */
  async function login(payload: LoginPayloadInterface) {
    const { $api } = useNuxtApp()
    const res = await $api<LoginResponseInterface>('/admin/account/login', {
      method: 'POST',
      // 登入 endpoint 沒 token、401 = 帳密錯；$api 全域 retry: 1 + retryStatusCodes: [401]
      // 是給「token 過期 → renew 後 retry」用的，會讓錯誤密碼無謂多打一次 API + 多噴一條
      // browser 401 console error。此處明確關掉 retry。
      retry: 0,
      body: {
        email: payload.email,
        password: payload.password,
        keep_login: payload.keepLogin,
      },
    })
    const maxAge = payload.keepLogin ? TOKEN_MAX_AGE.keepLogin : TOKEN_MAX_AGE.default
    persistToken(res.token, maxAge)
    scheduleRenew()
    await fetchProfile()
  }

  /** 抓目前登入者 profile。 */
  async function fetchProfile() {
    const { $api } = useNuxtApp()
    profile.value = await $api<AdminProfileInterface>('/admin/account/profile')
  }

  /** 檢查 profile 是否包含任一指定角色（OR 邏輯）。profile 未載入或 roles 為 null 時回 false。 */
  function hasRole(roles: string[]): boolean {
    if (!profile.value || !profile.value.roles) return false
    return roles.some(r => profile.value!.roles!.includes(r))
  }

  /** 登出：通知後端（失敗也無妨），一律清除本地 token 與 profile。 */
  async function logout() {
    const { $api } = useNuxtApp()
    try {
      await $api('/admin/account/logout', { method: 'PUT' })
    }
    catch {
      // 後端登出失敗（token 已過期等）不影響本地清除
    }
    clearSession()
  }

  return {
    profile,
    token,
    login,
    fetchProfile,
    logout,
    clearSession,
    hasRole,
    renewToken,
    scheduleRenew,
    cancelRenew,
  }
})
